<template>
    <div class="text-sm font-mono whitespace-pre-wrap break-words text-foreground">
        <div v-for="(section, sectionIndex) in processedSections" :key="sectionIndex" class="mb-4">
            <!-- Section header for collapsible sections -->
            <div
                v-if="section.isCollapsible"
                @click="toggleSection(section.id)"
                @keydown.enter="toggleSection(section.id)"
                @keydown.space.prevent="toggleSection(section.id)"
                class="flex items-center gap-2 mb-2 cursor-pointer hover:bg-muted/50 p-2 rounded border-l-2 border-amber-500 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                tabindex="0"
                role="button"
                :aria-expanded="!message.collapsedSections.has(section.id)"
                :aria-label="`${message.collapsedSections.has(section.id) ? 'Expand' : 'Collapse'} ${section.title} section with ${section.lines.length} lines`">
                <component :is="message.collapsedSections.has(section.id) ? ChevronRight : ChevronDown" class="h-4 w-4" />
                <span class="font-medium text-amber-600 dark:text-amber-400">{{ section.title }}</span>
                <span class="text-xs text-muted-foreground">({{ section.lines.length }} lines)</span>
            </div>

            <!-- Section content -->
            <div
                v-if="!section.isCollapsible || !message.collapsedSections.has(section.id)"
                class="space-y-1"
                :aria-hidden="section.isCollapsible && message.collapsedSections.has(section.id)">
                <div
                    v-for="(line, lineIndex) in section.lines"
                    :key="`${sectionIndex}-${lineIndex}`"
                    :class="['leading-relaxed', section.isCollapsible ? 'ml-6' : '']">
                    <span v-html="highlightStackTraceLine(line, message.searchTerm)"></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { ChevronRight, ChevronDown } from 'lucide-vue-next';

const props = defineProps({
    message: {
        type: Object,
        required: true
    },
    searchTerm: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['toggle-section']);

// Process the message into collapsible sections
const processedSections = computed(() => {
    const lines = props.message.lines;
    const sections = [];
    let currentSection = null;

    lines.forEach((line, index) => {
        // Detect main error message
        if (isMainErrorLine(line)) {
            if (currentSection) sections.push(currentSection);
            currentSection = {
                id: `error-${index}`,
                title: 'Error Message',
                lines: [line],
                isCollapsible: false
            };
        }
        // Detect stack trace frames
        else if (isStackFrameLine(line)) {
            if (!currentSection || currentSection.title !== 'Stack Trace') {
                if (currentSection) sections.push(currentSection);
                currentSection = {
                    id: `stack-${index}`,
                    title: 'Stack Trace',
                    lines: [line],
                    isCollapsible: true
                };
            } else {
                currentSection.lines.push(line);
            }
        }
        // Detect caused by sections
        else if (isCausedByLine(line)) {
            if (currentSection) sections.push(currentSection);
            currentSection = {
                id: `caused-${index}`,
                title: 'Caused By',
                lines: [line],
                isCollapsible: true
            };
        }
        // Add to current section or create new
        else if (line.trim()) {
            if (!currentSection) {
                currentSection = {
                    id: `misc-${index}`,
                    title: 'Details',
                    lines: [line],
                    isCollapsible: false
                };
            } else {
                currentSection.lines.push(line);
            }
        }
    });

    if (currentSection) sections.push(currentSection);
    return sections;
});

function isMainErrorLine(line) {
    return /^\w+Error:/.test(line.trim()) || /^Error:/.test(line.trim());
}

function isStackFrameLine(line) {
    return /^\s*at\s/.test(line) || /\s+at .+\(.+:\d+:\d+\)/.test(line);
}

function isCausedByLine(line) {
    return /^Caused by:/.test(line.trim());
}

function highlightStackTraceLine(line, searchTerm) {
    let highlighted = line;

    // Highlight search term first
    if (searchTerm) {
        const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
        highlighted = highlighted.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 text-black dark:text-white">$1</mark>');
    }

    // Apply syntax highlighting patterns
    highlighted = highlighted
        // File paths with line numbers
        .replace(
            /(\(.+?\.(js|ts|vue|py|java|scala|rb|go|rs|cpp|c|php|cs|kt|swift):\d+:\d+\))/g,
            '<span class="text-blue-600 dark:text-blue-400 font-medium">$1</span>'
        )
        // Method/function names
        .replace(
            /(at\s+)([a-zA-Z_$][a-zA-Z0-9_$]*(?:\.[a-zA-Z_$][a-zA-Z0-9_$]*)*)/g,
            '$1<span class="text-purple-600 dark:text-purple-400 font-medium">$2</span>'
        )
        // Line numbers
        .replace(
            /:(\d+):(\d+)/g,
            ':<span class="text-amber-600 dark:text-amber-400 font-semibold">$1</span>:<span class="text-amber-600 dark:text-amber-400 font-semibold">$2</span>'
        )
        // Error types
        .replace(/(\w+Error):/g, '<span class="text-red-600 dark:text-red-400 font-semibold">$1</span>:')
        // "at" keywords
        .replace(/^\s*(at\s+)/, '<span class="text-gray-500 dark:text-gray-400">$1</span>');

    return highlighted;
}

function escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function toggleSection(sectionId) {
    emit('toggle-section', sectionId);
}
</script>
